import { z } from 'zod';
import type { ItemTipo } from './itens.js';

/**
 * Perfil de atividade da empresa (Sessao 24). SO EXIBICAO: define os tipos de item oferecidos no cadastro, o que aparece no menu e o
 * formato da ficha; nunca apaga dado e a API continua respondendo. Religar a atividade mostra tudo de novo.
 */
export const ATIVIDADES = ['INDUSTRIA', 'COMERCIO', 'SERVICO'] as const;
export const atividadeEmpresa = z.enum(ATIVIDADES);
export type AtividadeEmpresa = z.infer<typeof atividadeEmpresa>;

export const ROTULO_ATIVIDADE: Record<AtividadeEmpresa, string> = { INDUSTRIA: 'Indústria', COMERCIO: 'Comércio', SERVICO: 'Serviço' };

/** PUT /empresa/atividades: multipla escolha, ao menos uma, sem repetir. */
export const atividadesSchema = z.object({
  atividades: z
    .array(atividadeEmpresa)
    .min(1, 'Escolha ao menos uma atividade')
    .refine((a) => new Set(a).size === a.length, 'Atividade repetida'),
});
export type AtividadesDto = z.infer<typeof atividadesSchema>;

const INSUMOS: readonly ItemTipo[] = ['MATERIA_PRIMA', 'MATERIAL_SECUNDARIO', 'EMBALAGEM', 'MATERIAL_AUXILIAR'];
const POR_ATIVIDADE: Record<AtividadeEmpresa, readonly ItemTipo[]> = {
  COMERCIO: ['MERCADORIA'],
  INDUSTRIA: ['PRODUTO', ...INSUMOS, 'SERVICO_TERCEIRO'],
  SERVICO: ['SERVICO', 'SERVICO_TERCEIRO'],
};
const ORDEM_TIPOS: readonly ItemTipo[] = ['PRODUTO', 'MERCADORIA', 'SERVICO', 'MATERIA_PRIMA', 'MATERIAL_SECUNDARIO', 'EMBALAGEM', 'MATERIAL_AUXILIAR', 'SERVICO_TERCEIRO'];

/** Tipos de item oferecidos no cadastro (uniao das atividades, na ordem de sempre). Sem atividade informada: todos. */
export function tiposDeItemOferecidos(atividades: readonly AtividadeEmpresa[] | undefined): ItemTipo[] {
  if (!atividades || atividades.length === 0) return [...ORDEM_TIPOS];
  const set = new Set(atividades.flatMap((a) => POR_ATIVIDADE[a]));
  return ORDEM_TIPOS.filter((t) => set.has(t));
}

/** Telas de custo industrial/servico (fichas, centros, rateio e CPM) somem do menu quando a empresa nao e industria nem servico. */
export const ROTAS_DE_PRODUCAO: readonly string[] = [
  '/custos/fichas-tecnicas',
  '/custos/centros',
  '/custos/criterios',
  '/custos/configuracoes-rateio',
  '/custos/cifs',
  '/custos/itens-cif',
  '/custos/rateios',
  '/plano/necessidade',
  '/plano/custo-fabrica',
];
/** Telas de compra de MP: somem quando a empresa tem atividades informadas e nenhuma delas e INDUSTRIA (ex.: so COMERCIO ou so SERVICO). */
export const ROTAS_DE_INDUSTRIA: readonly string[] = ['/plano/compras'];
export function mostraProducao(atividades: readonly AtividadeEmpresa[] | undefined): boolean {
  return !atividades || atividades.length === 0 || atividades.includes('INDUSTRIA') || atividades.includes('SERVICO');
}
/** A rota aparece no menu para esta empresa? (so exibicao; a rota continua acessivel por link.) */
export function rotaVisivelPorAtividade(rota: string, atividades: readonly AtividadeEmpresa[] | undefined): boolean {
  if (!atividades || atividades.length === 0) return true;
  if (ROTAS_DE_PRODUCAO.includes(rota) && !mostraProducao(atividades)) return false;
  if (ROTAS_DE_INDUSTRIA.includes(rota) && !atividades.includes('INDUSTRIA')) return false;
  return true;
}

